/**
 * Web: optional blob/http reads for processImageForSend; in-memory map for chat audio/video temp paths.
 */

const store = new Map<string, string>();

function keyPath(p: string): string {
  return p.replace(/^\/+/, '');
}

function isRemoteUri(p: string): boolean {
  return (
    p.startsWith('blob:') ||
    p.startsWith('http://') ||
    p.startsWith('https://') ||
    p.startsWith('data:')
  );
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));
    let piece = '';
    for (let j = 0; j < chunk.length; j += 1) {
      piece += String.fromCharCode(chunk[j]);
    }
    binary += piece;
  }
  return btoa(binary);
}

export default {
  CachesDirectoryPath: '/cache',
  DocumentDirectoryPath: '/documents',
  async exists(p: string): Promise<boolean> {
    const path = p.startsWith('file://') ? p.slice('file://'.length) : p;
    if (isRemoteUri(path)) return true;
    return store.has(keyPath(path));
  },
  async readFile(p: string, encoding?: string): Promise<string> {
    const path = p.startsWith('file://') ? p.slice('file://'.length) : p;
    if (isRemoteUri(path)) {
      if (path.startsWith('data:')) {
        const comma = path.indexOf(',');
        if (comma < 0) return '';
        const meta = path.slice(0, comma);
        const data = path.slice(comma + 1);
        if (meta.includes(';base64')) {
          return encoding === 'base64' ? data : atob(data);
        }
        return encoding === 'base64' ? btoa(decodeURIComponent(data)) : decodeURIComponent(data);
      }
      const res = await fetch(path);
      if (!res.ok) {
        throw new Error(`readFile failed: HTTP ${res.status}`);
      }
      const buf = await res.arrayBuffer();
      if (encoding === 'base64') {
        return arrayBufferToBase64(buf);
      }
      return new TextDecoder().decode(buf);
    }
    return store.get(keyPath(path)) ?? '';
  },
  async writeFile(p: string, data: string, _encoding?: string): Promise<void> {
    store.set(keyPath(p), data);
  },
  async unlink(p: string): Promise<void> {
    const path = p.startsWith('file://') ? p.slice('file://'.length) : p;
    if (path.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(path);
      } catch {
        /* noop */
      }
      return;
    }
    store.delete(keyPath(path));
  },
  async stat(p: string): Promise<{ size: number; isFile: () => boolean }> {
    const path = p.startsWith('file://') ? p.slice('file://'.length) : p;
    if (path.startsWith('blob:')) {
      try {
        const res = await fetch(path);
        const buf = await res.arrayBuffer();
        return { size: buf.byteLength, isFile: () => true };
      } catch {
        return { size: 0, isFile: () => true };
      }
    }
    const v = store.get(keyPath(path)) ?? '';
    return { size: v.length, isFile: () => true };
  },
};
